import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { checkSupportAccess, createTicket } from '../services/supportService';
import './CreateTicket.css';

const CATEGORIES = [
  { value: 'billing', label: 'Billing & Subscription' },
  { value: 'technical', label: 'Technical Issue / Bug Report' },
  { value: 'account', label: 'Account & Login' },
  { value: 'coaching', label: 'Coaching & Fitness' },
  { value: 'nutrition', label: 'Nutrition & Meal Plans' },
  { value: 'community_kitchen', label: 'Community Kitchen' },
  { value: 'general', label: 'General / Other' }
];

const PRIORITY_LABELS = { low: 'Low', medium: 'Medium', high: 'High', urgent: 'Urgent' };

const CreateTicket = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [allowedPriorities, setAllowedPriorities] = useState(['low', 'medium']);
  const [ckOnly, setCkOnly] = useState(false);
  const [subject, setSubject] = useState(searchParams.get('subject') || '');
  const [category, setCategory] = useState(searchParams.get('category') || '');
  const [priority, setPriority] = useState('medium');
  const [message, setMessage] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);

  useEffect(() => {
    loadAccess();
  }, []);

  const loadAccess = async () => {
    try {
      const data = await checkSupportAccess();
      if (!data.hasAccess) {
        toast.error('Support tickets require a paid subscription');
        navigate('/support');
        return;
      }
      if (data.ckOnly) {
        setCkOnly(true);
        setCategory('community_kitchen');
        setAllowedPriorities(['low', 'medium']);
      } else {
        setAllowedPriorities(data.allowedPriorities || ['low', 'medium']);
      }
    } catch (err) {
      toast.error('Failed to verify access');
      navigate('/support');
    } finally {
      setLoading(false);
    }
  };

  const handleScreenshotChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Only JPEG, PNG, and WebP images are allowed');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }

    setScreenshot(file);
    setScreenshotPreview(URL.createObjectURL(file));
  };

  const removeScreenshot = () => {
    setScreenshot(null);
    if (screenshotPreview) URL.revokeObjectURL(screenshotPreview);
    setScreenshotPreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!subject.trim()) return toast.error('Subject is required');
    if (!category) return toast.error('Please select a category');
    if (!message.trim()) return toast.error('Please describe your issue');

    setSubmitting(true);
    try {
      const data = await createTicket({ subject, category, priority, message, screenshot });
      toast.success(`Ticket ${data.ticket.ticketNumber} created!`);
      navigate(`/support/ticket/${data.ticket._id}`);
    } catch (err) {
      toast.error(err.message || 'Failed to create ticket');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="create-ticket-page">
        <div className="support-loading">
          <div className="loading-spinner" />
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="create-ticket-page">
      <div className="create-ticket-container">
        <div className="create-ticket-header">
          <button className="back-link" onClick={() => navigate('/support')}>
            ← Back to Support
          </button>
          <h1>New Support Ticket</h1>
          <p>Describe your issue and we'll get back to you as soon as possible.</p>
        </div>

        <form className="create-ticket-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="subject">Subject</label>
            <input
              id="subject"
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Brief summary of your issue"
              maxLength={200}
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="category">Category</label>
              <select
                id="category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                required
                disabled={ckOnly}
              >
                {ckOnly ? (
                  <option value="community_kitchen">Community Kitchen</option>
                ) : (
                  <>
                    <option value="">Select a category</option>
                    {CATEGORIES.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </>
                )}
              </select>
              {ckOnly && (
                <span className="form-hint">
                  Free plan — only Community Kitchen issues can be raised
                </span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="priority">Priority</label>
              <select
                id="priority"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
              >
                {allowedPriorities.map(p => (
                  <option key={p} value={p}>{PRIORITY_LABELS[p]}</option>
                ))}
              </select>
              {allowedPriorities.length <= 2 && (
                <span className="form-hint">
                  Upgrade to Premium for high & urgent priority access
                </span>
              )}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="message">Description</label>
            <textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Please describe your issue in detail. Include any steps to reproduce the problem, error messages, or relevant context."
              rows={8}
              maxLength={5000}
              required
            />
            <span className="form-hint">{message.length}/5000</span>
          </div>

          <div className="form-group">
            <label>Screenshot (optional)</label>
            {screenshotPreview ? (
              <div className="screenshot-preview">
                <img src={screenshotPreview} alt="Screenshot preview" />
                <button type="button" className="screenshot-remove" onClick={removeScreenshot}>
                  ✕ Remove
                </button>
              </div>
            ) : (
              <label className="screenshot-upload">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleScreenshotChange}
                  hidden
                />
                <span className="upload-icon">📷</span>
                <span>Click to attach a screenshot</span>
                <span className="upload-hint">JPEG, PNG, or WebP — max 5MB</span>
              </label>
            )}
          </div>

          <div className="form-actions">
            <button type="button" className="cancel-btn" onClick={() => navigate('/support')}>
              Cancel
            </button>
            <button type="submit" className="submit-btn" disabled={submitting}>
              {submitting ? 'Submitting...' : 'Submit Ticket'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateTicket;
